import BadgeUserPendingCertification from './BadgeUserPendingCertification';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import { useRequestDeleteUserImage } from '@/hooks/httpRequest';
import BadgeUserCertified from './BadgeUserCertified';

import { Button } from '@/components/ui/button.tsx';
import { IUserPicture } from '@/lib/interface.ts';
import Grid from '@/components/custom/Grid.tsx';
import { VerifStatusEnum } from '@/lib/enum.ts';
import ImgUserPicture from './ImgUserPicture';
import { getPhotoUrl } from '@/lib/utils.ts';
import { useEffect, useState } from 'react';
import { BadgeCheck } from 'lucide-react';

type TProps = {
	certifPhotoStatus?: VerifStatusEnum;
	showUnverifiedPictures?: boolean;
	imageContainerClassName?: string;
	pictures: IUserPicture[];
	certifPhoto?: string;
	className?: string;
	readonly?: boolean;
	userId: string;
}

const ProfilePictureItemGrid = ({ userId, pictures, certifPhoto, certifPhotoStatus, imageContainerClassName, className, showUnverifiedPictures = false, readonly = false }: TProps) => {

	const { triggerDeleteUserImage } = useRequestDeleteUserImage();
	const [userPictures, setUserPictures] = useState<IUserPicture[]>([]);
	const [deletingPosition, setDeletingPosition] = useState<string | null>(null);

	useEffect(
		() => {
			pictures &&
				setUserPictures(pictures);
		},
		[pictures]
	);

	const deletePicture = async (pic: IUserPicture) => {
		const yes = await confirm('Etes-vous sur de vouloir supprimer ?');
		if (!yes) return;
		
		// Start exit animation
		setDeletingPosition(pic.position);
		
		// Delay removal to allow animation to complete (300ms)
		setTimeout(() => {
			setUserPictures(prevState => prevState.filter(item => item.position !== pic.position));
			setDeletingPosition(null);
		}, 300);
		
		// Fire and forget - API call runs in background
		triggerDeleteUserImage({ userId, position: pic.position });
	};

	if (!certifPhoto && userPictures.length === 0) {
		return <></>;
	}

	return (
		<Grid className={`gap-2 md:gap-5 px-2 ${className}`}>
			{
				certifPhoto &&
				<div className={`w-28 md:w-36 relative ${imageContainerClassName}`}>
					<ImgUserPicture
						enableFullScreen
						alt={'user-image'}
						className={'rounded-md cursor-pointer'}
						src={getPhotoUrl(certifPhoto)} />
					<div className={'h-8 mt-1.5'} />
					{
						certifPhotoStatus === VerifStatusEnum.COMPLETED &&
						<span className={'absolute top-2 right-2'}>
							<BadgeUserCertified
								forceDisplay />
						</span>
					}
					{
						certifPhotoStatus === VerifStatusEnum.PENDING &&
						<span className={'absolute top-2 right-0'}>
							<BadgeUserPendingCertification
								forceDisplay
								mode='banner' />
						</span>
					}
				</div>
			}
			{
				[...userPictures]
					.filter(pic => pic.status !== VerifStatusEnum.PENDING || showUnverifiedPictures)
					.map(
						(pic, index) => (
							<div
								key={`${pic.path}.${index}`}
								className={`w-28 md:w-36 relative transition-all duration-300 ${imageContainerClassName} ${
									deletingPosition === pic.position ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
								}`}>
								<div className={'relative'}>
									<ImgUserPicture
										enableFullScreen
										alt={'user-image'}
										className={'rounded-md cursor-pointer'}
										src={getPhotoUrl(pic.path)} />
									{/* Verified images appear with a checkmark */}
									{
										pic.status !== VerifStatusEnum.PENDING &&
										(
											pic.verifiedBy
												? (
													<div className={'absolute bottom-1 left-1 right-1'}>
														<div className={'flex items-center space-x-1 bg-white text-xs rounded p-0.5'}>
															<BadgeCheck
																size={16}
																fill='#9333ea'
																color='white' />
															<span className='overflow-hidden truncate'>
																Par: {pic.verifiedBy?.username}
															</span>
														</div>
													</div>
												)
												: (
													<span className={'absolute top-0.5 right-0.5 shadow-lg'}>
														<BadgeCheck
															size={16}
															fill='#9333ea'
															color='white' />
													</span>
												)
										)
									}
								</div>
								{
									!readonly &&
									<Button
										disabled={deletingPosition === pic.position}
										onClick={() => deletePicture(pic)}
										className={'w-full text-sm h-8 text-red-500 bg-red-200 hover:bg-red-100 mt-1.5'}>
										Supprimer
									</Button>
								}
							</div>
						)
					)
			}
		</Grid>
	);

};

export default ProfilePictureItemGrid;
